import {
  useGetMediaGridQuery,
  type Media,
} from "@package/ui-gql/index-data-access";
import { useMemo } from "react";

export const MEDIA_ROOT_LABEL = "Media Library";

// Folders are fetched in a single page so the whole tree can be built
// client-side; libraries with more folders than this will show a partial tree.
export const MEDIA_FOLDER_LIMIT = 1000;

function compareByName(a: Media, b: Media) {
  return a.name.localeCompare(b.name);
}

export function useMediaFolders() {
  const { data, loading, error } = useGetMediaGridQuery({
    variables: {
      filter: { isDir: true },
      perPage: MEDIA_FOLDER_LIMIT,
    },
  });

  const folders = useMemo(
    () => (data?.items ?? []).filter((folder) => !!folder.id),
    [data?.items],
  );

  const rootFolder = useMemo(
    () => folders.find((folder) => folder.root),
    [folders],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map<string, Media[]>();
    folders.forEach((folder) => {
      if (!folder.parent) return;
      const siblings = map.get(folder.parent);
      if (siblings) {
        siblings.push(folder);
      } else {
        map.set(folder.parent, [folder]);
      }
    });
    map.forEach((siblings) => siblings.sort(compareByName));
    return map;
  }, [folders]);

  const truncated = (data?.total?.count ?? 0) > MEDIA_FOLDER_LIMIT;

  return { folders, rootFolder, childrenByParent, loading, error, truncated };
}
